import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ConnectionError from "../../components/ConnectionError/ConnectionError";
import useReveal from "../../hooks/useReveal";
import { publicContentService } from "../../services/publicContentService";
import type { ServiceItem } from "../../types";
import "./Services.css";

function Services() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  const loadServices = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await publicContentService.getServices();
      setItems(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useReveal([items.length, loading]);

  return (
    <main className="page-main">
      <section className="section services-section">
        <div className="container">
          <h1 className="section-title reveal">{t("services.title")}</h1>
          <p className="services-subtitle reveal">{t("services.subtitle")}</p>
          <div className="services-divider"></div>
        </div>

        <div className="services-wide">
          {loading ? (
            <div className="admin-loading">
              <span className="auth-spinner auth-spinner-brand"></span>
            </div>
          ) : error ? (
            <ConnectionError onRetry={loadServices} />
          ) : (
            <div className="services-stack">
              {items.map((item) => {
                const title = isArabic ? item.titleAr : item.titleEn;
                const desc = isArabic ? item.descAr : item.descEn;
                return (
                  <Link
                    key={item.id}
                    to={`/services/${item.id}`}
                    className="service-block reveal"
                  >
                    {item.imageUrl && (
                      <div className="service-block-img">
                        <img src={item.imageUrl} alt={title} />
                      </div>
                    )}
                    <div className="service-block-text">
                      <h3>{title}</h3>
                      <p>{desc}</p>
                      <span className="service-block-cta">
                        {t("services.viewProducts")}
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="container">
          <div className="services-bottom reveal">
            <p>{t("services.book")}</p>
            <Link to="/consultation" className="btn btn-filled">
              {t("services.cta")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Services;
